// Everything that belongs to the signed-in person goes when they do.
//
// Owner, 2026-10-01: « I was logged out and I got a notification showing up
// even though I was logged out ». Two things outlived the sign-out:
//
//   1. THE SESSION ITSELF. auth-js `signOut()` keeps the stored session when
//      Supabase cannot be told (no network, 5xx, 429) and only returns an
//      error, which the store ignored. The screen said « signed out »; the
//      next time the tab became visible auth-js found the session on disk,
//      announced it as SIGNED_IN, and the admin was silently back.
//   2. WHAT WAS LOADED FOR THEM. The notification feed kept its channel for a
//      10 s grace, the permission set was cached for the page's lifetime (the
//      next person inherited the admin's), and React Query kept every screen.
//
// The store calls `runSignedOutCleanup()` on EVERY way of becoming signed out
// (the button, another tab, an expired session); each module that holds
// per-person state registers its own wipe here. A registry rather than direct
// imports because those modules read the auth store themselves.
//
// Guard: store/signOut.test.tsx
import { AUTH_STORAGE_KEY } from './supabase';

const cleanups = new Set<() => void>();

/** Register a wipe to run whenever the person signs out. */
export function onSignedOut(fn: () => void): void {
  cleanups.add(fn);
}

/** Run every wipe. One that throws never stops the others. */
export function runSignedOutCleanup(): void {
  for (const fn of cleanups) {
    try { fn(); } catch (err) { console.error('[auth] sign-out cleanup failed:', err); }
  }
}

/**
 * Remove the session auth-js keeps on disk. Used only when Supabase could not
 * confirm the sign-out — after that, nothing on this device can bring the
 * session back (a tab focus, a token refresh). The refresh token is still
 * valid on Supabase's side until it expires; it just no longer exists here.
 */
export function forgetStoredSession(): void {
  for (const key of [AUTH_STORAGE_KEY, `${AUTH_STORAGE_KEY}-code-verifier`, `${AUTH_STORAGE_KEY}-user`]) {
    try { localStorage.removeItem(key); } catch { /* storage blocked: nothing was stored either */ }
  }
}
